import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Header } from '@/pages/public/landing/components/Header';
import { Footer } from '@/pages/public/landing/components/Footer';
import { cn } from '@/lib/utils';
import { TEIA_IMAGE_SRC } from './teiaImageSrc';

const pageCls =
	'text-[15px] leading-[1.65] text-[#1A1A1A] antialiased selection:bg-[#E06A30] selection:text-white';

const IN_PAGE_NAV = [
	{ href: '#dominios', label: 'Domínios' },
	{ href: '#mapa', label: 'Indicadores' },
	{ href: '#adequados', label: 'Adequados' },
	{ href: '#atencao-1', label: 'Atenção' },
	{ href: '#cbi', label: 'Esgotamento' },
] as const;

const DOMAIN_ITEMS: ReadonlyArray<{
	num: string;
	title: string;
	body: string;
}> = [
	{
		num: '01',
		title: 'Exigências no trabalho',
		body: 'Mede o quanto o trabalho cobra das pessoas em três frentes: volume de tarefas, ritmo da jornada e carga emocional do que precisa ser enfrentado.',
	},
	{
		num: '02',
		title: 'Organização e conteúdo do trabalho',
		body: 'Mede se a pessoa tem autonomia para decidir como faz seu trabalho, se aprende coisas novas, e se enxerga sentido no que faz.',
	},
	{
		num: '03',
		title: 'Relações interpessoais e liderança',
		body: 'Mede a qualidade dos vínculos com colegas e a percepção sobre apoio, clareza e justiça da chefia direta.',
	},
	{
		num: '04',
		title: 'Interface trabalho-indivíduo',
		body: 'Mede a fronteira entre trabalho e vida pessoal: se o trabalho invade o tempo de casa, e se a pessoa sente que dá conta.',
	},
	{
		num: '05',
		title: 'Valores no local de trabalho',
		body: 'Mede o sentimento de justiça, confiança nos colegas e nos superiores, e respeito mútuo na convivência.',
	},
	{
		num: '06',
		title: 'Saúde e bem-estar',
		body: 'Mede como a pessoa avalia a própria saúde no geral e a presença de sintomas físicos e emocionais nas últimas semanas.',
	},
	{
		num: '07',
		title: 'Comportamentos ofensivos',
		body: 'Mede a ocorrência de assédio moral, assédio sexual, ameaças e violência no trabalho.',
	},
];

type HeatZone = 'green' | 'yellow';

const HEAT_ROWS: ReadonlyArray<{
	zone: HeatZone;
	score: string;
	name: string;
	desc: string;
	inverted?: boolean;
}> = [
	{
		zone: 'yellow',
		score: '3,0',
		name: 'Burnout e estresse',
		desc: 'Frequência de exaustão e estresse vividos pelas pessoas no último mês.',
	},
	{
		zone: 'yellow',
		score: '2,7',
		name: 'Exigências no trabalho',
		desc: 'Volume, ritmo e carga emocional cobrados das pessoas no dia a dia.',
	},
	{
		zone: 'yellow',
		score: '2,7',
		name: 'Conflito trabalho-família',
		desc: 'Quanto o trabalho consome a energia que sobra para a vida pessoal.',
	},
	{
		zone: 'yellow',
		score: '2,6',
		name: 'Saúde geral',
		desc: 'Como cada pessoa avalia a própria saúde de modo geral.',
	},
	{
		zone: 'green',
		score: '1,6',
		name: 'Satisfação no trabalho',
		desc: 'Avaliação geral do trabalho. Escala invertida — nota baixa indica resposta positiva.',
		inverted: true,
	},
	{
		zone: 'green',
		score: '1,0',
		name: 'Comportamentos ofensivos',
		desc: 'Relato de assédio, violência ou atenção indesejada — escore mínimo da escala.',
		inverted: true,
	},
	{
		zone: 'green',
		score: '4,0',
		name: 'Influência e desenvolvimento',
		desc: 'Autonomia para decidir como o trabalho é feito e oportunidade de aprender.',
	},
	{
		zone: 'green',
		score: '4,4',
		name: 'Liderança',
		desc: 'Apoio, clareza e organização da chefia direta.',
	},
	{
		zone: 'green',
		score: '4,5',
		name: 'Relações interpessoais',
		desc: 'Qualidade dos vínculos com colegas no dia a dia.',
	},
	{
		zone: 'green',
		score: '4,7',
		name: 'Valores no local de trabalho',
		desc: 'Justiça, confiança e respeito mútuo entre as pessoas.',
	},
	{
		zone: 'green',
		score: '4,7',
		name: 'Significado e compromisso',
		desc: 'Propósito no trabalho e vínculo com a empresa.',
	},
];

const GREEN_CARDS: ReadonlyArray<{
	name: string;
	score: string;
	desc: string;
}> = [
	{
		name: 'Significado e compromisso',
		score: '4,7',
		desc: 'As pessoas sentem que o trabalho tem propósito e se importam com a empresa.',
	},
	{
		name: 'Valores no local de trabalho',
		score: '4,7',
		desc: 'Sentimento de justiça, confiança e respeito mútuo entre as pessoas.',
	},
	{
		name: 'Relações interpessoais',
		score: '4,5',
		desc: 'Qualidade dos vínculos e do apoio entre colegas no dia a dia.',
	},
	{
		name: 'Liderança',
		score: '4,4',
		desc: 'Apoio, clareza e organização da chefia direta.',
	},
	{
		name: 'Influência e desenvolvimento',
		score: '4,0',
		desc: 'Autonomia para decidir como o trabalho é feito e oportunidade de aprender.',
	},
	{
		name: 'Satisfação no trabalho',
		score: '1,6',
		desc: 'Avaliação geral do trabalho. Escala invertida — nota baixa é resposta positiva.',
	},
	{
		name: 'Comportamentos ofensivos',
		score: '1,0',
		desc: 'Ninguém relatou ter sofrido assédio, violência ou atenção indesejada nas últimas quatro semanas.',
	},
];

const FRENTE_TOOLS: ReadonlyArray<{
	num: string;
	title: string;
	desc: string;
	tools: ReadonlyArray<{ name: string; what: string }>;
}> = [
	{
		num: 'Frente 01',
		title: 'Reorganização do calendário',
		desc: 'Bloqueio de janelas fixas no dia para trabalho profundo, sem reuniões nem mensagens. Permite execução contínua das tarefas mais exigentes.',
		tools: [
			{
				name: 'Google Calendar',
				what: 'Permite criar blocos de "Tempo de foco" que silenciam notificações e recusam novas reuniões automaticamente.',
			},
			{
				name: 'Microsoft 365',
				what: 'Recurso "Viva Insights" sugere e protege blocos de foco com base na agenda da pessoa.',
			},
			{
				name: 'Reclaim.ai',
				what: 'Reorganiza automaticamente a agenda para preservar tempo dedicado a tarefas prioritárias.',
			},
		],
	},
	{
		num: 'Frente 02',
		title: 'Política de blocos de foco',
		desc: 'Acordo coletivo entre a equipe definindo um período fixo do dia sem reunião e sem mensagem. Reduz interrupções para todos ao mesmo tempo.',
		tools: [
			{
				name: 'Clockwise',
				what: 'Sincroniza calendários da equipe e cria blocos de foco protegidos coletivamente.',
			},
			{
				name: 'Microsoft Teams',
				what: 'Modo "Não perturbar" e horários focais aplicáveis ao time inteiro.',
			},
			{
				name: 'Notion Calendar',
				what: 'Visualização compartilhada de janelas indisponíveis, sem precisar negociar caso a caso.',
			},
		],
	},
	{
		num: 'Frente 03',
		title: 'Organização dos canais de comunicação',
		desc: 'Definição clara de qual canal serve a qual tipo de comunicação: urgente, assíncrono, social. Estabelece expectativa de tempo de resposta.',
		tools: [
			{
				name: 'Slack',
				what: 'Canais separados por urgência e tema, com horários de silêncio configuráveis.',
			},
			{
				name: 'Microsoft Teams',
				what: 'Estrutura de equipes e canais por área, com mensagens prioritárias diferenciadas.',
			},
			{
				name: 'Twist',
				what: 'Comunicação assíncrona por padrão, separa conversa de notificação imediata.',
			},
		],
	},
];

const FSSB_ITEMS: ReadonlyArray<{ num: string; title: string; desc: string }> =
	[
		{
			num: '01',
			title: 'Apoio emocional',
			desc: 'Reconhecer quando o colaborador está em momento difícil na vida pessoal. Escuta antes de cobrança.',
		},
		{
			num: '02',
			title: 'Apoio instrumental',
			desc: 'Flexibilizar quando possível — horário, modo de trabalho, prazo. Soluções práticas, não promessas.',
		},
		{
			num: '03',
			title: 'Modelagem de comportamento',
			desc: 'O líder dá o exemplo. Não envia mensagem fora do horário. Tira férias de verdade. Respeita os próprios limites.',
		},
		{
			num: '04',
			title: 'Gestão criativa',
			desc: 'Desenhar soluções que protejam o colaborador sem prejudicar a entrega. Não é abrir mão — é redesenhar.',
		},
	];

const TIMELINE_STEPS: ReadonlyArray<{
	quarter: string;
	action: string;
	desc: string;
}> = [
	{
		quarter: 'Etapa 01',
		action: 'Triagens periódicas',
		desc: 'Aplicação em ciclos curtos, dirigida ao tema específico identificado.',
	},
	{
		quarter: 'Etapa 02',
		action: 'Curva de evolução',
		desc: 'Acompanhamento da tendência ao longo do tempo, em vez de fotografias isoladas.',
	},
	{
		quarter: 'Etapa 03',
		action: 'Sinal antecipado',
		desc: 'Identificação de quando a curva começa a subir, antes de virar quadro clínico.',
	},
	{
		quarter: 'Etapa 04',
		action: 'Resposta dirigida',
		desc: 'Conversa estruturada, ajuste organizacional ou encaminhamento clínico.',
	},
];

function SectionEyebrow({ children }: { children: ReactNode }) {
	return (
		<div className="mb-6 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FA8046]">
			{children}
		</div>
	);
}

function SectionTitle({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<h2
			className={cn(
				'!font-libre mb-5 max-w-[900px] text-[clamp(1.75rem,4vw,2.5rem)] font-normal leading-[1.2] tracking-[-0.015em] text-[#1A1A1A]',
				className,
			)}
		>
			{children}
		</h2>
	);
}

function SectionLead({ children }: { children: ReactNode }) {
	return (
		<p className="mb-12 max-w-[760px] text-base leading-[1.65] text-[#4A4A4A]">
			{children}
		</p>
	);
}

function IncludedBanner({ children }: { children: ReactNode }) {
	return (
		<div className="mt-7 flex flex-col items-start gap-6 rounded-md bg-[#1A1A1A] px-8 py-6 text-[#FFFFFD] sm:flex-row sm:items-center">
			<div className="shrink-0 whitespace-nowrap rounded-sm bg-[#FA8046] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-white">
				Incluso no plano contínuo
			</div>
			<p className="text-[14.5px] leading-relaxed [&_strong]:font-semibold">
				{children}
			</p>
		</div>
	);
}

function HeatRowBlock({
	zone,
	score,
	name,
	desc,
	inverted,
}: {
	zone: HeatZone;
	score: string;
	name: string;
	desc: string;
	inverted?: boolean;
}) {
	const cellBg = zone === 'green' ? 'bg-[#E8F2EC]' : 'bg-[#FBF1DA]';
	const zoneLabel = zone === 'green' ? 'Adequado' : 'Atenção';
	const zoneCls = zone === 'green' ? 'text-[#2D8B5F]' : 'text-[#8B6E1F]';

	return (
		<div className="grid grid-cols-[minmax(90px,120px)_1fr] bg-white">
			<div
				className={cn(
					'flex flex-col items-center justify-center px-3 py-6 sm:px-3.5 sm:py-6',
					cellBg,
				)}
			>
				<div className="!font-libre text-[clamp(1.5rem,4vw,2rem)] font-semibold leading-none text-[#1A1A1A]">
					{score}
					<sup className="ml-0.5 text-[13px] font-normal text-[#767676]">
						/5
					</sup>
				</div>
				<div
					className={cn(
						'mt-2 text-[9px] font-semibold uppercase tracking-[0.22em]',
						zoneCls,
					)}
				>
					{zoneLabel}
				</div>
			</div>
			<div className="flex flex-col justify-center px-4 py-5 sm:px-7 sm:py-5">
				<div className="mb-1 text-base font-semibold text-[#1A1A1A]">
					{name}
				</div>
				<p className="text-sm leading-relaxed text-[#4A4A4A]">{desc}</p>
				{inverted ? (
					<span className="mt-1.5 inline-block text-[9px] font-medium uppercase tracking-[0.15em] text-[#767676]">
						Escala invertida
					</span>
				) : null}
			</div>
		</div>
	);
}

function DemonstracaoDevolutivaPage() {
	const location = useLocation();

	/** “Ver mais” da apresentação (secção 03) abre com #atencao-1; também cobre entrada após a senha. */
	useEffect(() => {
		const id = location.hash?.slice(1);
		if (!id) return;
		const t = window.setTimeout(() => {
			document.getElementById(id)?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest',
			});
		}, 120);
		return () => window.clearTimeout(t);
	}, [location.hash, location.key]);

	return (
		<>
			<Helmet>
				<title>Plumaa · Devolutiva editorial (demonstração)</title>
				<meta
					name="description"
					content="Demonstração da devolutiva editorial: domínios COPSOQ, mapa por subcategoria, pontos de atenção e CBI em linguagem de gestão."
				/>
			</Helmet>
			<Header />
			<div
				className={cn(pageCls)}
				style={{
					backgroundColor: '#F7F4EE',
					backgroundImage:
						'radial-gradient(ellipse 900px 600px at 92% 3%, #FFEAE0 0%, transparent 60%), radial-gradient(ellipse 500px 500px at 5% 95%, rgba(250,128,70,0.05) 0%, transparent 70%)',
					backgroundAttachment: 'fixed',
				}}
			>
				<nav
					aria-label="Seções da devolutiva"
					className="sticky top-16 z-40 border-b border-black/[0.08] bg-[rgba(255,255,253,0.95)] backdrop-blur-[14px]"
				>
					<div className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-end gap-6 px-6 py-[18px] md:px-12">
						{IN_PAGE_NAV.map((item) => (
							<a
								key={item.href}
								href={item.href}
								className="text-[11px] font-medium uppercase tracking-[0.1em] text-[#4A4A4A] opacity-70 transition hover:text-[#FA8046] hover:opacity-100"
							>
								{item.label}
							</a>
						))}
					</div>
				</nav>

				<header className="mx-auto flex min-h-[min(90vh,880px)] max-w-[1280px] flex-col justify-center px-6 pb-16 pt-10 md:px-20 md:pb-20 md:pt-24">
					<SectionEyebrow>
						Diagnóstico Psicossocial · Abril 2026
					</SectionEyebrow>
					<h1 className="!font-libre mb-7 max-w-[920px] text-[clamp(2rem,6vw,4rem)] font-normal leading-[1.1] tracking-[-0.02em] text-[#1A1A1A]">
						Resultado da avaliação
						<br />
						aplicada à equipe da [sua empresa].
					</h1>
					<p className="mb-14 max-w-[720px] text-[17px] leading-[1.7] text-[#4A4A4A]">
						Coleta realizada com cinco colaboradores ao longo do mês
						de abril, utilizando a versão curta do COPSOQ II-Br
						combinada ao Copenhagen Burnout Inventory.
					</p>
					<div className="grid w-full max-w-[900px] grid-cols-2 gap-y-8 border-t border-black/[0.08] pt-9 md:grid-cols-4">
						{[
							['Participação', '5 / 5'],
							['Indicadores adequados', '7'],
							['Pontos de atenção', '4'],
							['Indicadores críticos', '0'],
						].map(([label, value], index) => (
							<div
								key={label}
								className={cn(
									'border-black/[0.08] pr-8 md:border-r md:last:border-r-0',
									index > 0 && 'md:pl-10',
								)}
							>
								<strong className="mb-3 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#767676]">
									{label}
								</strong>
								<span className="!font-libre text-[28px] font-medium leading-none text-[#1A1A1A]">
									{value}
								</span>
							</div>
						))}
					</div>
				</header>

				<section
					id="dominios"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<SectionEyebrow>
							Visão geral por domínios
						</SectionEyebrow>
						<SectionTitle>
							Resultado consolidado nos sete domínios da
							avaliação.
						</SectionTitle>
						<SectionLead>
							A representação gráfica abaixo mostra o resultado da
							[sua empresa] em cada domínio. Pontos próximos do
							centro indicam adequação; pontos próximos da borda
							indicam pressão. A linha tracejada marca o limiar de
							atenção definido pelo instrumento.
						</SectionLead>
						<div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:items-start">
							<div className="rounded-md border border-black/[0.08] bg-white p-6">
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									COPSOQ II-Br · Resultado por domínio
								</div>
								<img
									src={TEIA_IMAGE_SRC}
									alt="Gráfico de teia com o resultado psicossocial da [sua empresa] por domínio"
									className="block h-auto w-full"
								/>
								<p className="mt-4 border-t border-black/[0.08] pt-4 text-xs leading-relaxed text-[#767676]">
									Score médio por domínio (escala 1–5). Mais
									alto = pior; fora da linha tracejada = pior.
								</p>
							</div>
							<ul className="list-none">
								{DOMAIN_ITEMS.map((d) => (
									<li
										key={d.num}
										className="grid grid-cols-[32px_1fr] items-start gap-[18px] border-b border-black/[0.08] py-[18px] last:border-b-0"
									>
										<span className="!font-libre pt-0.5 text-lg font-medium italic text-[#FA8046]">
											{d.num}
										</span>
										<div>
											<strong className="mb-1.5 block text-[15px] font-semibold text-[#1A1A1A]">
												{d.title}
											</strong>
											<span className="text-sm leading-relaxed text-[#4A4A4A]">
												{d.body}
											</span>
										</div>
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				<section
					id="mapa"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<SectionEyebrow>
							Resultados por subcategoria
						</SectionEyebrow>
						<SectionTitle>
							Detalhamento das onze subcategorias avaliadas.
						</SectionTitle>
						<SectionLead>
							Cada subcategoria recebe uma classificação por cor:
							verde indica adequação, amarelo indica atenção,
							vermelho indica ação prioritária.
						</SectionLead>

						<div className="mb-10 rounded-sm border-l-[3px] border-[#FA8046] bg-[#F7F4EE] px-6 py-6 md:px-7">
							<div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#FA8046]">
								Sobre a versão aplicada
							</div>
							<p className="text-[14.5px] leading-relaxed text-[#1A1A1A] [&_strong]:font-semibold">
								Esta avaliação utiliza a{' '}
								<strong>versão curta do COPSOQ II-Br</strong>,
								com 40 perguntas distribuídas em 11
								subcategorias — disponibilizada gratuitamente no
								primeiro mês como demonstração. A{' '}
								<strong>versão média</strong>, mais utilizada no
								Brasil para diagnósticos aprofundados, contempla{' '}
								<strong>76 perguntas em 29 subescalas</strong> e
								está disponível para empresas no plano contínuo.
							</p>
						</div>

						<div className="overflow-hidden rounded-md bg-black/[0.08] p-0.5">
							<div className="flex flex-col gap-0.5">
								{HEAT_ROWS.map((row) => (
									<HeatRowBlock key={row.name} {...row} />
								))}
							</div>
						</div>

						<div className="mt-7 flex flex-wrap gap-8 text-xs font-medium text-[#4A4A4A]">
							<div className="flex items-center gap-2">
								<span
									className="size-2.5 rounded-full bg-[#2D8B5F]"
									aria-hidden
								/>
								Verde — adequado
							</div>
							<div className="flex items-center gap-2">
								<span
									className="size-2.5 rounded-full bg-[#E0A52E]"
									aria-hidden
								/>
								Amarelo — atenção
							</div>
							<div className="flex items-center gap-2">
								<span
									className="size-2.5 rounded-full bg-[#C8362F]"
									aria-hidden
								/>
								Vermelho — ação prioritária
							</div>
						</div>
					</div>
				</section>

				<section
					id="adequados"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<SectionEyebrow>Indicadores adequados</SectionEyebrow>
						<SectionTitle>
							Sete subcategorias em zona verde.
						</SectionTitle>
						<SectionLead>
							Cinco delas com nota acima de 4,0. Conjunto que
							sustenta o ambiente psicossocial atual da empresa.
						</SectionLead>
						<div className="grid gap-5 md:grid-cols-2">
							{GREEN_CARDS.map((c) => (
								<div
									key={c.name}
									className="rounded-md border border-l-[3px] border-black/[0.08] border-l-[#2D8B5F] bg-white p-7"
								>
									<div className="mb-3 flex flex-wrap items-baseline justify-between gap-4">
										<span className="text-base font-semibold text-[#1A1A1A]">
											{c.name}
										</span>
										<span className="!font-libre text-[32px] font-semibold leading-none text-[#2D8B5F]">
											{c.score}
											<sup className="ml-0.5 text-[13px] font-normal text-[#767676]">
												/5
											</sup>
										</span>
									</div>
									<p className="text-sm leading-relaxed text-[#4A4A4A]">
										{c.desc}
									</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Atenção 01 */}
				<section
					id="atencao-1"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:min-h-0 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<div className="mb-12 grid gap-10 border-b border-black/[0.08] pb-10 md:grid-cols-[1fr_auto] md:items-end">
							<div>
								<div className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8B6E1F]">
									Ponto de atenção · 01 de 04
								</div>
								<h2 className="!font-libre mb-4 text-[clamp(1.75rem,4vw,2.75rem)] font-normal leading-[1.15] tracking-[-0.02em] text-[#1A1A1A]">
									Exigências no trabalho
								</h2>
								<p className="max-w-[580px] text-base leading-relaxed text-[#4A4A4A]">
									Volume de tarefas, ritmo da jornada e carga
									emocional do que precisa ser enfrentado.
								</p>
							</div>
							<div className="rounded-md bg-[#FBF1DA] px-9 py-7 text-center md:min-w-[200px]">
								<div className="!font-libre text-[64px] font-semibold leading-none tracking-[-0.02em] text-[#1A1A1A]">
									2,7
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/5
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8B6E1F]">
									Zona amarela
								</div>
							</div>
						</div>
						<div className="mb-14 grid gap-14 md:grid-cols-2">
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que mede
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									Volume de tarefas, ritmo durante a jornada
									de trabalho e situações emocionalmente
									desgastantes vividas pelas pessoas.
								</p>
							</div>
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que apareceu na [sua empresa]
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									A questão central não é o volume, e sim o
									ritmo. As pessoas{' '}
									<strong>
										não relatam excesso de tarefas
									</strong>
									; relatam{' '}
									<strong>
										necessidade de execução acelerada
									</strong>{' '}
									ao longo da jornada.
								</p>
							</div>
						</div>
						<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FA8046]">
							Intervenção sugerida
						</div>
						<h3 className="!font-libre mb-8 text-[clamp(1.35rem,3vw,1.75rem)] font-medium leading-snug text-[#1A1A1A]">
							Três frentes de organização do trabalho
						</h3>
						<div className="mb-7 grid gap-[18px] md:grid-cols-3">
							{FRENTE_TOOLS.map((f) => (
								<div
									key={f.num}
									className="flex flex-col rounded-md border border-t-[3px] border-black/[0.08] border-t-[#FA8046] bg-white p-7"
								>
									<div className="!font-libre mb-2 text-[13px] font-medium italic text-[#FA8046]">
										{f.num}
									</div>
									<div className="mb-3 text-[17px] font-semibold leading-snug text-[#1A1A1A]">
										{f.title}
									</div>
									<p className="mb-5 flex-1 text-sm leading-relaxed text-[#4A4A4A]">
										{f.desc}
									</p>
									<div className="mb-4 h-px bg-black/[0.08]" />
									<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#767676]">
										Ferramentas sugeridas
									</div>
									<ul className="list-none">
										{f.tools.map((t) => (
											<li
												key={t.name}
												className="border-b border-black/[0.08] py-2.5 last:border-b-0 last:pb-0"
											>
												<span className="mb-0.5 block text-sm font-semibold text-[#1A1A1A]">
													{t.name}
												</span>
												<span className="block text-[12.5px] leading-relaxed text-[#767676]">
													{t.what}
												</span>
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
						<IncludedBanner>
							<strong>Mapeamento personalizado</strong> das três
							frentes, adaptado ao perfil da empresa, com
							indicação das ferramentas mais adequadas ao ambiente
							já em uso pela equipe.
						</IncludedBanner>
					</div>
				</section>

				{/* Atenção 02 */}
				<section
					id="atencao-2"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<div className="mb-12 grid gap-10 border-b border-black/[0.08] pb-10 md:grid-cols-[1fr_auto] md:items-end">
							<div>
								<div className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8B6E1F]">
									Ponto de atenção · 02 de 04
								</div>
								<h2 className="!font-libre mb-4 text-[clamp(1.75rem,4vw,2.75rem)] font-normal leading-[1.15] tracking-[-0.02em] text-[#1A1A1A]">
									Conflito trabalho-família
								</h2>
								<p className="max-w-[580px] text-base leading-relaxed text-[#4A4A4A]">
									Quanto o trabalho consome a energia que
									sobra para a vida pessoal.
								</p>
							</div>
							<div className="rounded-md bg-[#FBF1DA] px-9 py-7 text-center md:min-w-[200px]">
								<div className="!font-libre text-[64px] font-semibold leading-none tracking-[-0.02em] text-[#1A1A1A]">
									2,7
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/5
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8B6E1F]">
									Zona amarela
								</div>
							</div>
						</div>
						<div className="mb-14 grid gap-14 md:grid-cols-2">
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que mede
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									Quanto o trabalho consome energia e tempo
									que faltam para a vida fora dele —
									relacionamentos, descanso e atenção plena no
									ambiente doméstico.
								</p>
							</div>
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que apareceu na [sua empresa]
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									Os colaboradores relatam que{' '}
									<strong>levam o trabalho para casa</strong>{' '}
									— não necessariamente o material, mas a
									atenção mental e a tensão emocional do dia.
								</p>
							</div>
						</div>
						<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FA8046]">
							Intervenção sugerida
						</div>
						<h3 className="!font-libre mb-8 text-[clamp(1.35rem,3vw,1.75rem)] font-medium leading-snug text-[#1A1A1A]">
							Capacitação de lideranças em comportamentos de apoio
							à vida pessoal (FSSB)
						</h3>
						<div className="mb-6 grid gap-[18px] md:grid-cols-2">
							{FSSB_ITEMS.map((it) => (
								<div
									key={it.num}
									className="flex gap-[18px] rounded-md border border-l-[3px] border-black/[0.08] border-l-[#FA8046] bg-white p-6 md:p-7"
								>
									<div className="!font-libre min-w-[26px] text-[22px] font-medium italic leading-none text-[#FA8046]">
										{it.num}
									</div>
									<div>
										<div className="mb-2 text-[15px] font-semibold text-[#1A1A1A]">
											{it.title}
										</div>
										<p className="text-[13.5px] leading-relaxed text-[#4A4A4A]">
											{it.desc}
										</p>
									</div>
								</div>
							))}
						</div>
						<p className="mb-6 rounded-md bg-[#F7F4EE] px-5 py-4 text-[13px] italic leading-relaxed text-[#4A4A4A]">
							FSSB (Family-Supportive Supervisor Behaviors) é uma
							das intervenções organizacionais com maior base
							científica para reduzir conflito trabalho-família,
							com efeitos comprovados também em saúde física,
							satisfação no trabalho e retenção de talentos.
						</p>
						<IncludedBanner>
							<strong>
								Treinamento online de 60 a 90 minutos
							</strong>{' '}
							para líderes diretos, cartilha de práticas adaptada
							à empresa, e questionário de acompanhamento aplicado
							antes e depois da capacitação.
						</IncludedBanner>
					</div>
				</section>

				{/* Atenção 03 */}
				<section
					id="atencao-3"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<div className="mb-12 grid gap-10 border-b border-black/[0.08] pb-10 md:grid-cols-[1fr_auto] md:items-end">
							<div>
								<div className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8B6E1F]">
									Ponto de atenção · 03 de 04
								</div>
								<h2 className="!font-libre mb-4 text-[clamp(1.75rem,4vw,2.75rem)] font-normal leading-[1.15] tracking-[-0.02em] text-[#1A1A1A]">
									Burnout e estresse
								</h2>
								<p className="max-w-[580px] text-base leading-relaxed text-[#4A4A4A]">
									Frequência de exaustão e estresse vividos
									pelas pessoas no último mês.
								</p>
							</div>
							<div className="rounded-md bg-[#FBF1DA] px-9 py-7 text-center md:min-w-[200px]">
								<div className="!font-libre text-[64px] font-semibold leading-none tracking-[-0.02em] text-[#1A1A1A]">
									3,0
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/5
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8B6E1F]">
									Zona amarela
								</div>
							</div>
						</div>
						<div className="mb-14 grid gap-14 md:grid-cols-2">
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que mede
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									Frequência com que as pessoas se sentiram
									fisicamente exaustas, emocionalmente
									esgotadas, estressadas e irritadas nas
									últimas quatro semanas.
								</p>
							</div>
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que apareceu na [sua empresa]
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									Os colaboradores relatam sentir esses
									estados em torno de{' '}
									<strong>&quot;às vezes&quot;</strong>. É o
									sinal mais antecipado de desgaste em
									acúmulo.
								</p>
							</div>
						</div>
						<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FA8046]">
							Intervenção sugerida
						</div>
						<h3 className="!font-libre mb-6 text-[clamp(1.35rem,3vw,1.75rem)] font-medium leading-snug text-[#1A1A1A]">
							Acompanhamento contínuo com triagens periódicas
						</h3>
						<p className="mb-8 max-w-[780px] text-base leading-[1.7] text-[#1A1A1A]">
							Aplicação de <strong>questionários curtos</strong>{' '}
							em intervalos regulares ao longo do ano, focados em
							sinais de exaustão e estresse. Diferente do
							diagnóstico completo, são triagens rápidas que
							mostram como a curva está evoluindo — se piorando,
							melhorando ou estável.
						</p>
						<div className="mb-7 grid gap-3.5 md:grid-cols-4">
							{TIMELINE_STEPS.map((s) => (
								<div
									key={s.quarter}
									className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#FA8046] bg-white px-4 py-6 text-center md:px-[18px]"
								>
									<div className="!font-libre mb-2.5 text-xs font-medium italic text-[#FA8046]">
										{s.quarter}
									</div>
									<div className="mb-2 text-[14.5px] font-semibold leading-snug text-[#1A1A1A]">
										{s.action}
									</div>
									<p className="text-[12.5px] leading-relaxed text-[#4A4A4A]">
										{s.desc}
									</p>
								</div>
							))}
						</div>
						<IncludedBanner>
							Aplicação de{' '}
							<strong>triagens curtas e periódicas</strong> ao
							longo do ano, com painel de evolução visível para o
							RH — para que o burnout seja detectado em curva, não
							em fotografia.
						</IncludedBanner>
					</div>
				</section>

				{/* Atenção 04 */}
				<section
					id="atencao-4"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<div className="mb-12 grid gap-10 border-b border-black/[0.08] pb-10 md:grid-cols-[1fr_auto] md:items-end">
							<div>
								<div className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8B6E1F]">
									Ponto de atenção · 04 de 04
								</div>
								<h2 className="!font-libre mb-4 text-[clamp(1.75rem,4vw,2.75rem)] font-normal leading-[1.15] tracking-[-0.02em] text-[#1A1A1A]">
									Saúde geral
								</h2>
								<p className="max-w-[580px] text-base leading-relaxed text-[#4A4A4A]">
									Como cada pessoa avalia a própria saúde no
									geral.
								</p>
							</div>
							<div className="rounded-md bg-[#FBF1DA] px-9 py-7 text-center md:min-w-[200px]">
								<div className="!font-libre text-[64px] font-semibold leading-none tracking-[-0.02em] text-[#1A1A1A]">
									2,6
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/5
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#8B6E1F]">
									Zona amarela
								</div>
							</div>
						</div>
						<div className="mb-14 grid gap-14 md:grid-cols-2">
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									Por que essa pergunta importa
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									A forma como a pessoa avalia a própria saúde
									tem peso técnico — não é palpite. Quando a
									pessoa diz que sua saúde está
									&quot;razoável&quot; ou &quot;ruim&quot;,
									isso costuma{' '}
									<strong>
										anteceder em meses sintomas físicos mais
										claros
									</strong>
									: queda de imunidade, alterações de sono,
									fadiga persistente.
								</p>
							</div>
							<div>
								<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#767676]">
									O que apareceu na [sua empresa]
								</div>
								<p className="text-base leading-[1.7] text-[#1A1A1A]">
									A maioria se avalia entre &quot;muito
									boa&quot; e &quot;boa&quot;. Algumas
									avaliações puxam o resultado para baixo —
									sinal de que{' '}
									<strong>
										parte das pessoas já não está se
										sentindo no auge
									</strong>
									.
								</p>
							</div>
						</div>
						<div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FA8046]">
							Intervenção sugerida
						</div>
						<h3 className="!font-libre mb-8 text-[clamp(1.35rem,3vw,1.75rem)] font-medium leading-snug text-[#1A1A1A]">
							Acesso clínico individual e ações educativas
							coletivas
						</h3>
						<div className="mb-7 grid gap-5 md:grid-cols-2">
							<div className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#FA8046] bg-white p-8">
								<div className="mb-3.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#FA8046]">
									Cuidado individual
								</div>
								<h4 className="!font-libre mb-[18px] text-[22px] font-medium leading-snug text-[#1A1A1A]">
									Acesso clínico em coparticipação
								</h4>
								<ul className="list-none space-y-0">
									{[
										'Plano de saúde mental ativado para os colaboradores',
										'Atendimento começa com avaliação por psicólogo da rede Plumaa',
										'Encaminhamento direto se houver indicação psiquiátrica ou clínica geral',
										'Relatórios trimestrais agregados e anônimos para o RH',
									].map((line) => (
										<li
											key={line}
											className="relative border-b border-black/[0.08] py-2.5 pl-[18px] text-sm leading-relaxed text-[#1A1A1A] before:absolute before:left-0 before:font-medium before:text-[#FA8046] before:content-['—'] last:border-b-0"
										>
											{line}
										</li>
									))}
								</ul>
							</div>
							<div className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#FA8046] bg-white p-8">
								<div className="mb-3.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#FA8046]">
									Educação coletiva
								</div>
								<h4 className="!font-libre mb-[18px] text-[22px] font-medium leading-snug text-[#1A1A1A]">
									Palestras temáticas dirigidas
								</h4>
								<ul className="list-none">
									{[
										'Sono e recuperação',
										'Alimentação consciente no dia a dia',
										'Atividade física e energia',
										'Ansiedade e estresse no contexto do trabalho',
									].map((line) => (
										<li
											key={line}
											className="relative border-b border-black/[0.08] py-2.5 pl-[18px] text-sm leading-relaxed text-[#1A1A1A] before:absolute before:left-0 before:font-medium before:text-[#FA8046] before:content-['—'] last:border-b-0"
										>
											{line}
										</li>
									))}
								</ul>
							</div>
						</div>
						<IncludedBanner>
							<strong>
								Acesso clínico e palestras combinados.
							</strong>{' '}
							O acesso individual funciona como porta de entrada
							para cuidado real, e as palestras como reforço
							educativo periódico aplicado conforme demanda
							detectada nos relatórios.
						</IncludedBanner>
					</div>
				</section>

				<section
					id="cbi"
					className="scroll-mt-32 border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24"
				>
					<div className="mx-auto max-w-[1280px]">
						<SectionEyebrow>Índice de esgotamento</SectionEyebrow>
						<SectionTitle>
							Resultado do Copenhagen Burnout Inventory.
						</SectionTitle>
						<SectionLead>
							A escala vai de 0 a 100. Quanto maior, pior. Abaixo
							de 50 indica risco baixo; entre 50 e 74, risco
							moderado; 75 ou mais, risco alto.
						</SectionLead>
						<div className="mb-7 grid gap-5 md:grid-cols-3">
							<div className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#E0A52E] bg-white px-7 py-8 text-center">
								<div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#767676]">
									Vida pessoal
								</div>
								<div className="!font-libre text-[76px] font-semibold leading-none text-[#1A1A1A]">
									50
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/100
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8B6E1F]">
									No limite
								</div>
								<p className="mt-[18px] border-t border-black/[0.08] pt-[18px] text-[13.5px] leading-relaxed text-[#4A4A4A]">
									Cansaço geral da vida, não restrito ao
									trabalho. É a área mais alta das três —
									exatamente no ponto onde a escala muda de
									baixo para moderado.
								</p>
							</div>
							<div className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#2D8B5F] bg-white px-7 py-8 text-center">
								<div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#767676]">
									Vida laboral
								</div>
								<div className="!font-libre text-[76px] font-semibold leading-none text-[#1A1A1A]">
									42
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/100
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2D8B5F]">
									Risco baixo
								</div>
								<p className="mt-[18px] border-t border-black/[0.08] pt-[18px] text-[13.5px] leading-relaxed text-[#4A4A4A]">
									Cansaço causado especificamente pelo
									trabalho. O trabalho em si não é hoje a
									fonte principal de exaustão.
								</p>
							</div>
							<div className="rounded-md border border-t-[3px] border-black/[0.08] border-t-[#2D8B5F] bg-white px-7 py-8 text-center">
								<div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#767676]">
									Relação com pessoas
								</div>
								<div className="!font-libre text-[76px] font-semibold leading-none text-[#1A1A1A]">
									30
									<sup className="ml-0.5 text-xl font-normal text-[#767676]">
										/100
									</sup>
								</div>
								<div className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2D8B5F]">
									Risco baixo
								</div>
								<p className="mt-[18px] border-t border-black/[0.08] pt-[18px] text-[13.5px] leading-relaxed text-[#4A4A4A]">
									Cansaço associado ao convívio profissional.
									As relações na [sua empresa] não estão
									pesando.
								</p>
							</div>
						</div>
						<div className="grid gap-10 rounded-md border border-l-[3px] border-black/[0.08] border-l-[#2D8B5F] bg-white p-10 md:grid-cols-[200px_1fr] md:items-center md:gap-10">
							<div className="!font-libre text-center text-[96px] font-semibold leading-none tracking-[-0.02em] text-[#2D8B5F] md:text-left">
								41
								<sup className="ml-0.5 text-2xl font-normal text-[#767676]">
									/100
								</sup>
							</div>
							<div>
								<strong className="!font-libre mb-3 block text-[22px] font-medium text-[#1A1A1A]">
									Índice consolidado — risco baixo.
								</strong>
								<p className="text-[15px] leading-[1.7] text-[#4A4A4A]">
									O índice consolidado é tranquilizador, mas o
									resultado de 50 na vida pessoal merece
									atenção. É o ponto da escala que separa
									risco baixo de moderado — onde dá para
									intervir antes do quadro avançar.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="border-t border-black/[0.08] px-6 py-16 md:px-20 md:py-24">
					<div className="mx-auto max-w-[1280px]">
						<SectionEyebrow>Encerramento</SectionEyebrow>
						<h2 className="!font-libre mb-6 max-w-[720px] text-[clamp(1.5rem,3vw,2.25rem)] font-normal leading-snug tracking-[-0.015em] text-[#1A1A1A]">
							Esse é o produto entregue pela Plumaa para cada
							empresa parceira da rede.
						</h2>
						<p className="mb-12 max-w-[640px] text-base leading-relaxed text-[#4A4A4A]">
							Painel completo, devolutiva técnica e plano de ação
							dirigido aos achados específicos da empresa
							avaliada. O conteúdo apresentado nesta devolutiva
							foi gerado a partir das respostas reais dos
							colaboradores da [sua empresa].
						</p>
						<div className="flex items-center gap-3.5 border-t border-black/[0.08] pt-8 text-[13px] font-medium text-[#767676]">
							<span className="font-semibold text-[#1A1A1A]">
								Plumaa
							</span>
							<span>Saúde Mental Corporativa</span>
						</div>
					</div>
				</section>
			</div>
			<Footer />
		</>
	);
}

export { DemonstracaoDevolutivaPage };
